import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  BulkUpdateTasksInput,
  CreateTaskInput,
  Paginated,
  ReorderTasksInput,
  TaskActivityItem,
  TaskAttachmentItem,
  TaskCommentItem,
  TaskDetail,
  TaskItem,
  TaskSectionItem,
  TaskWorkspace,
  UpdateTaskInput,
} from '@crm/shared';
import { api } from '../../api/client';

export interface TaskParams {
  status?: 'open' | 'done' | 'all';
  assignee?: 'me' | 'all';
  due?: 'overdue' | 'today' | 'week' | 'all';
  contactId?: string;
  organizationId?: string;
  dealId?: string;
  page?: number;
  pageSize?: number;
}

export function useTasks(params: TaskParams) {
  return useQuery({
    queryKey: ['tasks', 'list', params],
    queryFn: async () => (await api.get<Paginated<TaskItem>>('/tasks', { params })).data,
  });
}

export interface WorkspaceParams {
  scope: 'project' | 'mine';
  status: 'open' | 'done' | 'all';
}

const workspaceKey = (p: WorkspaceParams) => ['tasks', 'workspace', p] as const;

/** Every task of the current project (or of the signed-in user), for the list, board and calendar views. */
export function useTaskWorkspace(params: WorkspaceParams) {
  return useQuery({
    queryKey: workspaceKey(params),
    // finished tasks pile up: "all" shows the last 30 days of them
    queryFn: async () => (await api.get<TaskWorkspace>('/tasks/workspace', { params: { ...params, doneWithinDays: params.status === 'open' ? 0 : 30 } })).data,
    placeholderData: (prev) => prev,
  });
}

export function useTaskDetail(id: string | null | undefined) {
  return useQuery({
    queryKey: ['tasks', 'detail', id],
    queryFn: async () => (await api.get<TaskDetail>(`/tasks/${id}`)).data,
    enabled: !!id,
  });
}

export function useTaskActivity(mine: boolean) {
  return useInfiniteQuery({
    queryKey: ['task-activity', mine],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) =>
      (await api.get<{ items: TaskActivityItem[]; hasMore: boolean }>('/tasks/activity', { params: { mine, before: pageParam, limit: 50 } })).data,
    getNextPageParam: (last) => (last.hasMore ? last.items[last.items.length - 1]?.createdAt : undefined),
  });
}

function invalidate(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: ['tasks'] });
  void qc.invalidateQueries({ queryKey: ['task-activity'] });
  void qc.invalidateQueries({ queryKey: ['activities'] });
  void qc.invalidateQueries({ queryKey: ['dashboard'] });
}

function useInvalidateTasks() {
  const qc = useQueryClient();
  return () => invalidate(qc);
}

/** Applies a change to every cached workspace at once, so drags and ticks show before the server answers. */
function patchWorkspaces(qc: QueryClient, fn: (tasks: TaskItem[]) => TaskItem[]) {
  qc.setQueriesData<TaskWorkspace>({ queryKey: ['tasks', 'workspace'] }, (ws) => (ws ? { ...ws, tasks: fn(ws.tasks) } : ws));
}

/** Optimistic local version of a patch (the fields a list row shows). */
function applyPatch(t: TaskItem, patch: UpdateTaskInput, users?: Map<string, string>): TaskItem {
  const next = { ...t };
  if (patch.title !== undefined) next.title = patch.title;
  if (patch.dueAt !== undefined) next.dueAt = patch.dueAt;
  if (patch.priority !== undefined) next.priority = patch.priority;
  if (patch.sectionId !== undefined) next.sectionId = patch.sectionId;
  if (patch.projectId !== undefined) next.projectId = patch.projectId;
  if (patch.completed !== undefined) next.completedAt = patch.completed ? new Date().toISOString() : null;
  if (patch.assigneeUserId !== undefined) next.assignee = patch.assigneeUserId ? { id: patch.assigneeUserId, name: users?.get(patch.assigneeUserId) ?? '…' } : null;
  return next;
}

export function useCreateTask() {
  const inv = useInvalidateTasks();
  return useMutation({ mutationFn: async (input: CreateTaskInput) => (await api.post<TaskItem>('/tasks', input)).data, onSuccess: inv });
}

export function useUpdateTask(userNames?: Map<string, string>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateTaskInput & { id: string }) => (await api.patch<TaskItem>(`/tasks/${id}`, input)).data,
    onMutate: ({ id, ...patch }) => patchWorkspaces(qc, (tasks) => tasks.map((t) => (t.id === id ? applyPatch(t, patch, userNames) : t))),
    onSettled: () => invalidate(qc),
  });
}

export function useDeleteTask() {
  const inv = useInvalidateTasks();
  return useMutation({ mutationFn: async (id: string) => (await api.delete(`/tasks/${id}`)).data, onSuccess: inv });
}

export function useDuplicateTask() {
  const inv = useInvalidateTasks();
  return useMutation({ mutationFn: async (id: string) => (await api.post<TaskItem>(`/tasks/${id}/duplicate`)).data, onSuccess: inv });
}

export function useBulkUpdateTasks(userNames?: Map<string, string>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: BulkUpdateTasksInput) => (await api.post<{ updated: number }>('/tasks/bulk-update', input)).data,
    onMutate: ({ ids, patch }) => {
      const set = new Set(ids);
      patchWorkspaces(qc, (tasks) => tasks.map((t) => (set.has(t.id) ? applyPatch(t, patch, userNames) : t)));
    },
    onSettled: () => invalidate(qc),
  });
}

export function useBulkDeleteTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => (await api.post<{ deleted: number }>('/tasks/bulk-delete', { ids })).data,
    onMutate: (ids) => {
      const set = new Set(ids);
      patchWorkspaces(qc, (tasks) => tasks.filter((t) => !set.has(t.id) && !(t.parentId && set.has(t.parentId))));
    },
    onSettled: () => invalidate(qc),
  });
}

/** Saves a drag: the listed tasks, in this order, in this section (moving any that came from elsewhere). */
export function useReorderTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReorderTasksInput) => (await api.post('/tasks/reorder', input)).data,
    onMutate: (input) => {
      const index = new Map(input.ids.map((id, i) => [id, i]));
      patchWorkspaces(qc, (tasks) =>
        tasks.map((t) => (index.has(t.id) ? { ...t, position: index.get(t.id)!, ...(input.sectionId !== undefined ? { sectionId: input.sectionId ?? null } : {}) } : t)),
      );
    },
    onSettled: () => invalidate(qc),
  });
}

export function useAddComment(taskId: string) {
  const inv = useInvalidateTasks();
  return useMutation({ mutationFn: async (body: string) => (await api.post<TaskCommentItem>(`/tasks/${taskId}/comments`, { body })).data, onSuccess: inv });
}

export function useUpdateComment() {
  const inv = useInvalidateTasks();
  return useMutation({ mutationFn: async ({ id, body }: { id: string; body: string }) => (await api.patch<TaskCommentItem>(`/task-comments/${id}`, { body })).data, onSuccess: inv });
}

export function useDeleteComment() {
  const inv = useInvalidateTasks();
  return useMutation({ mutationFn: async (id: string) => (await api.delete(`/task-comments/${id}`)).data, onSuccess: inv });
}

export function useUploadAttachment(taskId: string) {
  const inv = useInvalidateTasks();
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return (await api.post<TaskAttachmentItem>(`/tasks/${taskId}/attachments`, form, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
    },
    onSuccess: inv,
  });
}

export function useDeleteAttachment() {
  const inv = useInvalidateTasks();
  return useMutation({ mutationFn: async (id: string) => (await api.delete(`/task-attachments/${id}`)).data, onSuccess: inv });
}

// -- sections ---------------------------------------------------------------------------------------------------

export function useCreateSection() {
  const inv = useInvalidateTasks();
  return useMutation({ mutationFn: async (input: { name: string; projectId?: string | null }) => (await api.post<TaskSectionItem>('/task-sections', input)).data, onSuccess: inv });
}

export function useRenameSection() {
  const inv = useInvalidateTasks();
  return useMutation({ mutationFn: async ({ id, name }: { id: string; name: string }) => (await api.patch<TaskSectionItem>(`/task-sections/${id}`, { name })).data, onSuccess: inv });
}

export function useDeleteSection() {
  const inv = useInvalidateTasks();
  return useMutation({ mutationFn: async (id: string) => (await api.delete(`/task-sections/${id}`)).data, onSuccess: inv });
}

export function useReorderSections() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => (await api.post('/task-sections/reorder', { ids })).data,
    onMutate: (ids) => {
      const index = new Map(ids.map((id, i) => [id, i]));
      qc.setQueriesData<TaskWorkspace>({ queryKey: ['tasks', 'workspace'] }, (ws) =>
        ws ? { ...ws, sections: ws.sections.map((s) => (index.has(s.id) ? { ...s, position: index.get(s.id)! } : s)).sort((a, b) => a.position - b.position) } : ws,
      );
    },
    onSettled: () => invalidate(qc),
  });
}

/** Sections a task can move to: those of its own project ("none" = tasks without a project). */
export function useProjectSections(projectId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ['tasks', 'sections', projectId ?? 'none'],
    queryFn: async () => (await api.get<TaskSectionItem[]>('/task-sections', { params: { projectId: projectId ?? 'none' } })).data,
    enabled,
  });
}
